// timezone-selector.component.ts
import { ChangeDetectionStrategy, Component, OnInit, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, of, Subject, take } from 'rxjs';
import { ITimeZone } from './timezone-interface';
import { TimezoneService } from './timezone-service';
import { AsyncPipe } from '@angular/common';
import { SelectModule } from 'primeng/select';

@Component({
    selector: 'unified-ui-timezone-selector',
    templateUrl: './timezone-selector.component.html',
    styleUrls: ['./timezone-selector.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        SelectModule,
        FormsModule
    ],
})
export class TimezoneSelectorComponent implements OnInit {
    private _timeZoneService = inject(TimezoneService);

    /**
     * Input of timezone selector component
     *
     * Must be standard IANA TimeZone (i.e. America/New_York).
     *
     * If you don't have the timezone, it will default to the users local timezone.
     */
    public readonly defaultTimeZoneName = input<string | undefined>(undefined);
    public readonly timeZoneChange = output<ITimeZone>();
    // We also expose this as an Observable
    private _timeZoneSubject = new Subject<ITimeZone | undefined>();

    public selectedTimeZone: ITimeZone | undefined = undefined;
    public timeZone$: Observable<ITimeZone | undefined> = this._timeZoneSubject.asObservable();
    public timeZones$: Observable<ITimeZone[]> = of([]);

    public ngOnInit(): void {
        this.timeZones$ = this._timeZoneService.getTimeZones();
        this.selectedTimeZone = this._timeZoneService.getUserDefaultTimeZone();
        const effectiveTimeZoneName = this.defaultTimeZoneName() ? this.defaultTimeZoneName() : this.selectedTimeZone.timeZone;
        this.setTimeZone(effectiveTimeZoneName);
    }

    public onTimeZoneChange() {
        this.timeZoneChange.emit(this.selectedTimeZone!);
        this._timeZoneSubject.next(this.selectedTimeZone);
    }

    private setTimeZone(timeZoneName?: string): void {
        const tzName = timeZoneName || this.defaultTimeZoneName();
        this.timeZones$.pipe(take(1)).subscribe(timeZones => {
            const defaultTimeZone = timeZones.find(tz => tz.timeZone === tzName);
            if (defaultTimeZone) {
                this.selectedTimeZone = defaultTimeZone;
                this.timeZoneChange.emit(this.selectedTimeZone);
                this._timeZoneSubject.next(this.selectedTimeZone);
            }
        });
    }
}
